import { createApp } from 'vue';
import TollCalculator from './components/TollCalculator.vue';
import FuelCalculator from './components/FuelCalculator.vue';
import TripPlanner from './components/TripPlanner.vue';

const tollEl = document.getElementById('toll-calculator');
if (tollEl) {
    createApp(TollCalculator).mount(tollEl);
}

const fuelEl = document.getElementById('fuel-calculator');
if (fuelEl) {
    createApp(FuelCalculator).mount(fuelEl);
}

const plannerEl = document.getElementById('trip-planner');
if (plannerEl) {
    createApp(TripPlanner).mount(plannerEl);
}

import SearchBox from './components/SearchBox.vue';

document.querySelectorAll('[data-search-box]').forEach((el) => {
    createApp(SearchBox, {
        action: el.dataset.action,
        initial: el.dataset.initial || '',
        variant: el.dataset.variant || 'header',
        placeholder: el.dataset.placeholder || 'Search routes, fees…',
    }).mount(el);
});

// ---------- Mobile search overlay ----------
// The checkbox already opens and closes it on its own, so this only adds the
// things CSS cannot do: move focus into the field, close on Escape, and stop
// the page behind from scrolling while the overlay is up.
const searchToggle = document.getElementById('search-toggle');
const searchOverlay = document.querySelector('.search-overlay');

if (searchToggle && searchOverlay) {
    const field = () => searchOverlay.querySelector('input[type="search"]');

    const sync = () => {
        const open = searchToggle.checked;
        document.body.style.overflow = open ? 'hidden' : '';
        searchOverlay.setAttribute('aria-hidden', open ? 'false' : 'true');
        // The checkbox is the announced control, so it owns the state.
        searchToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
            // Wait for the panel transition to start before focusing, or iOS
            // scrolls the page to a half-rendered element.
            requestAnimationFrame(() => field()?.focus());
        }
    };

    searchToggle.addEventListener('change', sync);
    sync();

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && searchToggle.checked) {
            // One Escape closes the suggestion list, a second closes the
            // overlay - so only close here when no list is showing.
            if (searchOverlay.querySelector('.suggest-list')) {
                return;
            }
            searchToggle.checked = false;
            sync();
        }
    });
}

// ---------- Ask bubble ----------
// Mounted only when the layout renders the placeholder, which it does only when
// the assistant is switched on in admin.
const askEl = document.getElementById('ask-bubble');
if (askEl) {
    import('./components/AskBubble.vue').then(({ default: AskBubble }) => {
        createApp(AskBubble, { endpoint: askEl.dataset.endpoint }).mount(askEl);
    });
}

// ---------- Page-level measurement ----------
// Delegated listeners rather than per-element hooks: these fire on plain
// server-rendered HTML, which is most of the site, and cost one listener each.
import { track, trackOnce } from './analytics';

// Scroll depth. GA4's enhanced measurement only reports a single 90% "scroll"
// event, which cannot tell a bounced visitor from one who read half the guide.
(() => {
    const milestones = [25, 50, 75, 100];
    let ticking = false;

    const check = () => {
        ticking = false;
        const doc = document.documentElement;
        const scrollable = doc.scrollHeight - window.innerHeight;
        if (scrollable < 400) return;          // short page: depth is meaningless

        const pct = ((window.scrollY / scrollable) * 100);

        for (const m of milestones) {
            if (pct >= m) trackOnce('depth:' + m, 'scroll_depth', { depth: m });
        }
    };

    window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(check);
    }, { passive: true });
})();

document.addEventListener('click', (e) => {
    // Navigation - which links actually carry people around the site.
    const navLink = e.target.closest('.main-nav a, .site-footer a, .nav-dropdown-menu a');
    if (navLink) {
        track('nav_click', {
            label: (navLink.textContent || '').trim(),
            href: navLink.getAttribute('href'),
            location: navLink.closest('.site-footer') ? 'footer' : 'header',
        });
        return;
    }

    // Opening the search overlay is intent, whether or not anything is typed.
    if (e.target.closest('.search-toggle-btn')) {
        track('search_open', { source: 'mobile_overlay' });
    }
}, { passive: true });

// FAQ accordions. <details> fires toggle, not click, and only the opening
// direction is interesting - which questions people actually have.
document.querySelectorAll('details.faq-item, details.route-details, details.fuel-extra').forEach((el) => {
    el.addEventListener('toggle', () => {
        if (!el.open) return;
        const label = (el.querySelector('summary')?.textContent || '').trim();
        track('accordion_open', { label });
    });
});

// Embed page: copying the snippet is the whole conversion for that page.
document.getElementById('embed-code')?.addEventListener('copy', () => {
    track('embed_code_copy', { method: 'select' });
});
document.querySelectorAll('[data-embed-copy]').forEach((btn) => {
    btn.addEventListener('click', () => track('embed_code_copy', { method: 'button' }));
});

// Contact form: submitted, and separately confirmed by the server on reload.
document.querySelector('.contact-form')?.addEventListener('submit', () => {
    track('contact_submit', {});
});
if (document.querySelector('.form-success')) {
    track('contact_success', {});
}

// ---------- Toll matrix search ----------
// Only pays for itself on pages that actually carry a matrix.
if (document.querySelector('[data-matrix]')) {
    import('./matrix-search').then(({ initMatrixSearch }) => initMatrixSearch());
}

// ---------- Installable app ----------
import { initPwa } from './pwa';
initPwa();

// Keep the menu button's announced state in step with the checkbox that holds it.
const navToggle = document.getElementById('nav-toggle');
if (navToggle) {
    const syncNav = () => navToggle.setAttribute('aria-expanded', navToggle.checked ? 'true' : 'false');
    navToggle.addEventListener('change', syncNav);
    syncNav();
}

// ---------- Fuel page: fill the toll without a reload ----------
// The links carry a working href, so this is an upgrade rather than a
// requirement: without JS the page simply reloads with ?toll= and prefills.
document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-toll-prefill]');
    if (!link) return;

    e.preventDefault();
    window.dispatchEvent(new CustomEvent('toll:prefill', { detail: link.dataset.tollPrefill }));

    const calc = document.getElementById('fuel-calculator');
    calc?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    track('result_action', { tool: 'fuel', action: 'trip_toll_prefill', fee: Number(link.dataset.tollPrefill) });
});
